(() => {
  'use strict';
  if (window.otfMotionReady) return;
  window.otfMotionReady = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function init(root = document) {
    root.querySelectorAll('[data-video-toggle]').forEach(button => {
      if (button.dataset.ready) return;
      const target = button.getAttribute('aria-controls');
      const video = (target && document.getElementById(target)) || button.parentElement.querySelector('video');
      if (!video) return;
      button.dataset.ready = 'true';
      // Keep the historical hero marker compatible with cached section markup.
      const hero = button.hasAttribute('data-video-pause-only');
      button.hidden = false;
      const media = video.parentElement;
      const responsiveHero = hero && video.hasAttribute('data-hero-responsive');
      const heroLayout = responsiveHero ? video.closest('[data-hero-overlay]') : null;
      const heroLink = heroLayout?.querySelector('.otf-hero-overlay__link');
      const heroBreakpoint = responsiveHero ? window.matchMedia('(max-width: 749px)') : null;
      let heroLayoutObserver;
      const fallback = media?.querySelector('[data-video-fallback]');
      const text = button.querySelector('[data-video-toggle-text]');
      const glyph = button.querySelector('[data-video-toggle-icon]');
      const playIcon = button.querySelector('svg.icon--play');
      const pauseIcon = button.querySelector('svg.icon--pause');
      const poster = media?.querySelector('.otf-hero-poster');
      const posterReady = !poster || poster.complete ? Promise.resolve() : new Promise(resolve => {
        poster.addEventListener('load', resolve, { once: true });
        poster.addEventListener('error', resolve, { once: true });
      });
      let inViewport = false;
      let disposed = false;
      let autoplayBlocked = false;
      let playbackVersion = 0;
      let observer;
      posterReady.then(() => {
        if (!disposed && poster?.naturalWidth) video.poster = poster.currentSrc;
      });
      const sync = () => {
        const paused = video.paused;
        const label = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
        if (hero) {
          // Keep the shopper's chosen frame visible when the responsive hero is manually paused.
          video.toggleAttribute('data-hero-playing', video.readyState >= 2 && (!paused || (responsiveHero && video.dataset.otfUserPaused === 'true')));
        }
        button.setAttribute('aria-label', label);
        button.dataset.state = paused ? 'paused' : 'playing';
        if (text) text.textContent = label; else button.setAttribute('aria-label', label);
        if (glyph) glyph.textContent = paused ? '▶' : 'Ⅱ';
        if (playIcon) playIcon.style.display = paused ? '' : 'none';
        if (pauseIcon) pauseIcon.style.display = paused ? 'none' : '';
        // The fallback is only for a refused autoplay, never for a pause the shopper chose or a reduced-motion preference.
        if (fallback) fallback.hidden = !(paused && autoplayBlocked && video.dataset.otfUserPaused !== 'true' && !reduced.matches);
      };
      const canAutoplay = () => inViewport && !document.hidden && !reduced.matches && video.dataset.otfUserPaused !== 'true' && (!hero || !autoplayBlocked);
      const canStart = explicit => !disposed && !document.hidden && video.dataset.otfUserPaused !== 'true' && (explicit ? (!observer || inViewport) : canAutoplay());
      const pause = () => { playbackVersion += 1; video.pause(); };
      const start = async (explicit = false) => {
        if (!canStart(explicit)) return;
        const version = ++playbackVersion;
        if (!explicit) {
          await posterReady;
          if (version !== playbackVersion || !canStart(false)) return;
        }
        if (!video.currentSrc) {
          const mobile = window.matchMedia('(max-width: 749px)').matches || heroLayout?.hasAttribute('data-hero-expanded');
          const source = mobile ? video.dataset.videoMobileSrc : video.dataset.videoDesktopSrc;
          if (source) {
            video.src = source;
            video.load();
          }
        }
        try {
          await video.play();
          if (version !== playbackVersion) return;
          if (!canStart(explicit)) { pause(); return; }
          autoplayBlocked = false;
        } catch (_) {
          if (version !== playbackVersion) return;
          if (!explicit) autoplayBlocked = true;
        }
        sync();
      };
      const onHeroLayout = () => {
        if (!heroLayout || !heroLink || disposed) return;
        const block = Math.ceil(heroLink.getBoundingClientRect().height);
        const expanded = block >= 64;
        heroLayout.toggleAttribute('data-hero-expanded', expanded);
        media.style.setProperty('--otf-hero-cta-block', block + 'px');
        const single = heroBreakpoint.matches || expanded;
        const source = single ? video.dataset.videoMobileSrc : video.dataset.videoDesktopSrc;
        const pictureSource = media.querySelector('.otf-hero-picture source');
        if (pictureSource) pictureSource.media = expanded ? 'not all' : '(min-width: 750px) and (scripting: enabled)';
        const nextPoster = single ? video.dataset.videoMobilePoster : video.dataset.videoDesktopPoster;
        if (nextPoster) video.poster = nextPoster;
        if (source && video.hasAttribute('src') && video.getAttribute('src') !== source) {
          pause();
          video.removeAttribute('data-hero-playing');
          video.src = source;
          video.load();
          start();
        }
        sync();
      };
      const onToggle = () => {
        video.dataset.otfUserPaused = video.paused ? 'false' : 'true';
        if (video.paused) start(true); else pause();
      };
      const onFallback = () => {
        video.dataset.otfUserPaused = 'false';
        start(true);
      };
      const onMotionChange = () => {
        if (reduced.matches) pause(); else start();
        sync();
      };
      const onVisibilityChange = () => {
        if (document.hidden) pause(); else start();
      };
      if (responsiveHero && heroLayout && heroLink) {
        heroBreakpoint.addEventListener('change', onHeroLayout);
        if ('ResizeObserver' in window) {
          heroLayoutObserver = new ResizeObserver(onHeroLayout);
          heroLayoutObserver.observe(heroLink);
          heroLayoutObserver.observe(heroLayout);
        }
        onHeroLayout();
      }
      video.addEventListener('play', sync);
      video.addEventListener('playing', sync);
      button.addEventListener('blur', sync);
      video.addEventListener('pause', sync);
      reduced.addEventListener('change', onMotionChange);
      document.addEventListener('visibilitychange', onVisibilityChange);
      button.addEventListener('click', onToggle);
      fallback?.addEventListener('click', onFallback);
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            inViewport = entry.isIntersecting;
            if (!inViewport) pause(); else start();
          });
        }, { threshold: 0.05 });
        observer.observe(video);
      } else {
        inViewport = !hero;
        if (!hero) start();
      }
      button._otfCleanup = () => {
        disposed = true;
        observer?.disconnect();
        heroLayoutObserver?.disconnect();
        heroBreakpoint?.removeEventListener('change', onHeroLayout);
        reduced.removeEventListener('change', onMotionChange);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        video.removeEventListener('play', sync);
        video.removeEventListener('playing', sync);
        button.removeEventListener('blur', sync);
        video.removeEventListener('pause', sync);
        button.removeEventListener('click', onToggle);
        fallback?.removeEventListener('click', onFallback);
        pause();
        delete button.dataset.ready;
      };
      if (reduced.matches || document.hidden) pause();
      sync();
    });
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-video-toggle]').forEach(button => button._otfCleanup?.()));
})();
